import React, { useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SortableListItem from './partials/SortableListItem';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { FormUtils } from '../Form';
import { Namespace } from '../../../translation/namespace';
import Btn, { BtnModifier } from '../Btn';
import { Icons } from '../../atoms/SVG';
import { TooltipPlacement } from '../../atoms/Tooltip';
import { isActionDisabled } from '../../blocks/ContentWrapper/ContentWrapper.utils';
import FormCheckbox from '../Form/components/FormCheckbox/FormCheckbox';
import FormsRow from '../Form/components/FormsRow/FormsRow';
import { moveElementInList, DragAndDropProvider } from '../../../_lib/DragAndDrop';
import { ContentWrapperAction as Action } from '../../blocks/ContentWrapper/ContentWrapper.types';
import { SkipLink, SkipLinkTarget } from '../../../_lib/components/SkipLink';

function getListItemMoveButton(action, disabled, options, t, targetId) {
  return {
    text: t(`actions.${action}.${disabled ? 'disabled' : 'text'}`, options),
    disabled,
    targetId,
  };
}

function renderSkipLinks(target, state, id, t) {
  if (state?.list?.length >= 5) {
    return (
      <SkipLink id={`sortable-list-${target}-${id}`} focusTarget>
        {t(`skipLinks.${target}`)}
      </SkipLink>
    );
  }
  return null;
}

function renderSkipLinkTarget(target, state, id, t) {
  if (state?.list?.length >= 5) {
    return <SkipLinkTarget id={`sortable-list-${target}-${id}`} targetName={t(`skipLinkTargets.${target}`)} />;
  }
  return null;
}

function createState(focusableItemId, alphabeticallySorted, list) {
  return {
    focusableItemId,
    alphabeticallySorted,
    list,
  };
}

function reducer(state, action) {
  if (action.resetPayload) {
    const { alphabeticallySorted, sortableList } = action.resetPayload;
    return createState(null, alphabeticallySorted, sortableList);
  }

  return createState(action.sourceId, false, moveElementInList(state.list, action.sourceId, action.targetId));
}

function SortableList({
  id,
  name,
  sortableList,
  preparedValue = null,
  onChange,
  heading,
  headingHierarchy = 3,
  initialList,
  intitiallySorted,
  sortableItemName = undefined,
  sortAlphabeticallyCheckBox,
  resetChanges,
  hasChanges,
}) {
  const { t } = useTranslation(Namespace.SortableList);
  const [state, dispatch] = useReducer(
    reducer,
    createState(null, sortAlphabeticallyCheckBox.checked, [...sortableList]),
  );
  const alphabeticalOrder = useRef(null);
  const initialOrder = useRef(null);

  if (alphabeticalOrder.current === null) {
    alphabeticalOrder.current = preparedValue
      ? preparedValue.map(element => element.id)
      : initialList.map(element => element.id);
  }

  if (initialOrder.current === null) {
    initialOrder.current = initialList.map(element => element.id);
  }

  useEffect(() => {
    onChange(name, state.list);
    if (!state.alphabeticallySorted) {
      sortAlphabeticallyCheckBox.onChange({ target: { checked: false } });
    }
  },
  [
    name,
    state.list,
    onChange,
    sortAlphabeticallyCheckBox.name,
    state.alphabeticallySorted,
    sortAlphabeticallyCheckBox,
  ]);

  /**
   * Reset sate with initially provided properties
   * and call the resetChanges callback if provided
   */
  const undoChanges = () => {
    if (resetChanges) {
      resetChanges();
    }

    dispatch({
      resetPayload: {
        sortableList: initialList,
        alphabeticallySorted: intitiallySorted,
      },
    });
  };

  const onChangeCheckbox = (event) => {
    if (event.target.checked) {
      const sortableList =
        preparedValue ||
        state.list.sort((a, b) => {
          const positionA = alphabeticalOrder.current.findIndex(id => id === a.id);
          const positionB = alphabeticalOrder.current.findIndex(id => id === b.id);

          return positionA - positionB;
        });

      dispatch({
        resetPayload: { sortableList, alphabeticallySorted: true },
      });
    }
  };

  return (
    <>
      <FormsRow>
        <FormCheckbox
          id={`sort-alphabetically-switch-${sortAlphabeticallyCheckBox.name}`}
          labelTitle={t('sortAlphabetically')}
          name={sortAlphabeticallyCheckBox.name}
          layout="switch"
          checked={sortAlphabeticallyCheckBox.checked}
          value={sortAlphabeticallyCheckBox.value}
          onChange={(event) => {
            sortAlphabeticallyCheckBox.onChange(event);
            onChangeCheckbox(event);
          }}
          ariaControls={`sortable-list-${id}`}
        />
      </FormsRow>
      <div className="sortable-list__title-container">
        <Heading hierarchy={headingHierarchy} className="sortable-list__title" id={`sortable-list-title-${id}`}>
          {heading}
        </Heading>
        <div className="sortable-list__undo-button">
          <Btn
            type="button"
            text={t('actions.reset.text')}
            titleText={t('actions.reset.text')}
            hiddenText
            tooltipPlacement={TooltipPlacement.TOP}
            modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
            svgSpriteId={Icons.UNDO}
            ariaDisabled={!hasChanges()}
            onClick={undoChanges}
            ariaControls={`sortable-list-${id}`}
            ariaDescribedBy={`sortable-list-title-${id}`}
          />
        </div>
      </div>
      {state?.list && (
        <div className="sortable-list__container">
          <div className="sortable-list__skiplinks">
            {renderSkipLinkTarget('top', state, id, t)}
            {renderSkipLinks('bottom', state, id, t)}
          </div>
          <DragAndDropProvider>
            <ol className="sortable-list" aria-labelledby={`sortable-list-title-${id}`} id={`sortable-list-${id}`}>
              {state.list.map((item, index) => {
                return (
                  <SortableListItem
                    key={item.id}
                    id={item.id}
                    name={item.name}
                    sortableListItem={item}
                    moveItem={(sourceId, targetId) => dispatch({ sourceId, targetId })}
                    focusableItemId={state?.focusableItemId}
                    // button configs
                    moveDownButton={getListItemMoveButton(
                      Action.moveBlockDown,
                      isActionDisabled(Action.moveBlockDown, index, state.list.length),
                      {
                        index: index + 1,
                        sortableItemName: sortableItemName || t('sortableItemName'),
                        position: index + 2,
                      },
                      t,
                      state.list.at(index + 1)?.id,
                    )}
                    moveUpButton={getListItemMoveButton(
                      Action.moveBlockUp,
                      isActionDisabled(Action.moveBlockUp, index, state.list.length),
                      {
                        index: index + 1,
                        sortableItemName: sortableItemName || t('sortableItemName'),
                        position: index,
                      },
                      t,
                      state.list.at(index - 1)?.id,
                    )}
                  />
                );
              })}
            </ol>
          </DragAndDropProvider>
          <div className="sortable-list__skiplinks">
            {renderSkipLinkTarget('bottom', state, id, t)}
            {renderSkipLinks('top', state, id, t)}
          </div>
          <input
            // this input must have a key, otherwise it won't change values
            key={id}
            type="hidden"
            name={name}
            value={state.list.map(element => element.id).join(', ')}
          />
        </div>
      )}
      {FormUtils.renderHiddenFields(sortableList)}
    </>
  );
}

SortableList.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  intitiallySorted: PropTypes.bool.isRequired,
  initialList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  sortableList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  preparedValue: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  headingHierarchy: PropTypes.number,
  sortableItemName: PropTypes.string,
  sortAlphabeticallyCheckBox: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.bool,
    checked: PropTypes.bool,
    onChange: PropTypes.any,
  }).isRequired,
  resetChanges: PropTypes.func.isRequired,
  hasChanges: PropTypes.func.isRequired,
};

export default SortableList;
